import React, { useState } from 'react'

import { twMerge } from '../../tailwind/merge'
import { Box } from '../box'
import { Pressable } from '../pressable'
import { Text } from '../text'
import {
    AttachmentPreviewModal,
    isImageAttachment,
    type PreviewAttachment,
} from './AttachmentPreviewModal.web'

export type AttachmentThumbnailGridProps = {
    attachments: PreviewAttachment[]
    columns: 3 | 4
    /** Hover caption over a thumbnail, e.g. "Preview". */
    previewLabel: string
    /** Caps the grid's width and height in px. */
    maxSize?: number
    /** Lays the thumbnails out from the right edge. */
    reversed?: boolean
}

const columnClass = { 3: 'grid-cols-3', 4: 'grid-cols-4' }

/**
 * Read-only grid of square thumbnails (image, or a pdf rendered in place) that
 * open the full-screen preview. Web only.
 */
export const AttachmentThumbnailGrid = ({
    attachments,
    columns,
    previewLabel,
    maxSize,
    reversed,
}: AttachmentThumbnailGridProps) => {
    const [previewIndex, setPreviewIndex] = useState(0)
    const [isPreviewOpen, setPreviewOpen] = useState(false)

    return (
        <>
            <Box
                className={twMerge(
                    'grid gap-3',
                    columnClass[columns],
                    reversed && 'grid-flow-dense [direction:rtl]',
                )}
                style={maxSize ? { maxWidth: maxSize, maxHeight: maxSize } : undefined}
            >
                {attachments.map((attachment, index) => (
                    <Pressable
                        key={index}
                        className='group aspect-square overflow-hidden rounded-lg bg-background-elevation'
                        accessibilityRole='button'
                        accessibilityLabel={previewLabel}
                        onPress={() => {
                            setPreviewIndex(index)
                            setPreviewOpen(true)
                        }}
                    >
                        {isImageAttachment(attachment) ? (
                            <img
                                src={attachment.link}
                                alt={attachment.name ?? ''}
                                className='absolute inset-0 h-full w-full object-cover'
                            />
                        ) : (
                            <iframe
                                src={`${attachment.link}#toolbar=0`}
                                title={attachment.name ?? ''}
                                className='absolute inset-0 h-full w-full border-0 bg-transparent'
                            />
                        )}
                        <Box className='absolute inset-0 items-center justify-center opacity-0 group-hover:opacity-100'>
                            <Box className='absolute inset-0 bg-background-canvas opacity-80' />
                            <Box>
                                <Text size='P3' tone='knockout'>
                                    {previewLabel}
                                </Text>
                            </Box>
                        </Box>
                    </Pressable>
                ))}
            </Box>
            <AttachmentPreviewModal
                isOpen={isPreviewOpen}
                attachments={attachments}
                attachmentIndex={previewIndex}
                editable={false}
                closeAction={() => setPreviewOpen(false)}
                onIndexChange={setPreviewIndex}
            />
        </>
    )
}
